/**
 * A return's states and its generated files, in words — one copy for Returns and Download.
 *
 * Found 2026-09-29: the Returns artifact list read `client workbook v1 · current ·
 * adae0fdf6d25e1f9… · from signed_off` — a hash prefix and a raw state — while Download named the
 * same files properly and printed the same raw state beside them. Both now read from here, and
 * `tests/seam-vocabularies.test.ts` holds each map total over the service's vocabulary.
 */

/** A period's state (`period_states`). */
export const STATE_LABELS: Record<string, string> = {
  open: 'Open',
  prepared: 'Prepared',
  signed_off: 'Signed off',
  sent_for_approval: 'Out for approval',
  client_approved: 'Client approved',
  filed: 'Filed',
  locked: 'Locked',
}

/** A generated file's kind (`artifact_kinds`). */
export const KIND_LABEL: Record<string, string> = {
  client_workbook: 'Client workbook',
  offline_utility: 'Offline utility workbook',
  gstn_json: 'GSTN JSON',
  filed_return: 'Filed return',
  gstr3b_working: 'GSTR-3B working paper',
  working_sheet: 'GSTR-1 working sheet',
  amendments: 'GSTR-1 amendments',
  // B-07 (FR-E13): the GSTR-1A payload — the month's correction, its changed rows only.
  gstr1a_json: 'GSTR-1A JSON',
  // C-09 / C-08 (FR-E24, FR-E23): the GSTR-3B's filing outputs.
  gstr3b_json: 'GSTR-3B JSON',
  gstr3b_return: 'GSTR-3B return sheet',
  gstr3b_challan: 'GSTR-3B challan working',
}

/** A return's name as a practitioner writes it (`return_types`). */
export const RETURN_NAMES: Record<string, string> = {
  GSTR1: 'GSTR-1',
  GSTR3B: 'GSTR-3B',
  // B-07 (FR-E13): a same-period correction of a filed GSTR-1, its own period.
  GSTR1A: 'GSTR-1A',
  // B-10 (FR-E16): a quarterly client's optional monthly Invoice Furnishing Facility.
  IFF: 'IFF',
  // Not a return: the QRMP monthly deposit, which the calendar names beside them.
  PMT06: 'PMT-06',
}

/** "GSTR-1A", never the service's key; an unknown type says it is one. */
export function returnName(returnType: string): string {
  return RETURN_NAMES[returnType] ?? 'A return ProGST has no name for'
}

/** `current` / `superseded` — the table's own check constraint allows these two. */
export const ARTIFACT_STATUS_LABEL: Record<string, string> = {
  current: 'Current',
  superseded: 'Replaced by a later version',
}

export interface ArtifactLike {
  kind: string
  version_no: number
  status: string
  generated_from_state: string
  /** N28: why it stopped being current, when a change to its inputs did that. */
  out_of_date_because?: string | null
}

/**
 * A file's status in words, among the period's other files. A superseded file was always
 * "Replaced by a later version" — untrue when nothing later exists, which is the state every
 * demotion and every change to a 3B's inputs leaves (N28): then it is out of date, and says why
 * where the service recorded it.
 */
export function artifactStatusWords(
  a: Pick<ArtifactLike, 'kind' | 'version_no' | 'status' | 'out_of_date_because'>,
  all: Array<Pick<ArtifactLike, 'kind' | 'version_no'>>,
): string {
  if (a.status !== 'superseded') return ARTIFACT_STATUS_LABEL[a.status] ?? a.status
  if (all.some((other) => other.kind === a.kind && other.version_no > a.version_no)) {
    return ARTIFACT_STATUS_LABEL.superseded!
  }
  return a.out_of_date_because
    ? `Out of date — generate it again (${a.out_of_date_because})`
    : 'Out of date — generate it again'
}

/** "Client workbook · version 2 · Current · made when the return was Signed off". */
export function artifactWords(a: ArtifactLike, all: ArtifactLike[] = [a]): string {
  return [
    KIND_LABEL[a.kind] ?? 'A file of a kind ProGST has no name for',
    `version ${a.version_no}`,
    artifactStatusWords(a, all),
    `made when the return was ${STATE_LABELS[a.generated_from_state] ?? a.generated_from_state}`,
  ].join(' · ')
}
